import { Diagnostic } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { useState, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { useRemoveAddIn } from "@/hooks/addins/useRemoveAddIn";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { getDiagnostics } from "@/hooks/diagnostic/useDiagnosticsBySearch";
import diagnosticsToPreset from "@/hooks/presets/diagnosticsToPreset";
import usePresets from "@/hooks/presets/usePresets";
import { Column, Preset } from "@/models/Preset";

const TABLE_HEIGHT = 300;

const PresetBulkManager: React.FC = () => {
  const { t } = useTranslation();
  const { data: presets, isLoading, error } = usePresets();
  const updateAddIn = useUpdateAddIn<Preset>(AddInIds.presets, "id");
  const removeAddIn = useRemoveAddIn(AddInIds.presets, "id");
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  const [importInProgress, setImportInProgress] = useState(false);
  const { geotabClient } = useGeotabClient();
  const [multipleMatchesDialog, setMultipleMatchesDialog] = useState<{
    open: boolean;
    options: Array<{ name: string; id: string }>;
    column: Column | null;
    presetId: string | null;
    resolve: (selected: { name: string; id: string } | null) => void;
  }>({
    open: false,
    options: [],
    column: null,
    presetId: null,
    resolve: () => {
      return;
    },
  });
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && presets) {
      const newSelecteds = presets.map(preset => preset.id);
      setSelectedPresets(newSelecteds);
    } else {
      setSelectedPresets([]);
    }
  };

  const handleClick = (id: string) => {
    setSelectedPresets(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id],
    );
  };

  const handleExport = () => {
    if (presets) {
      const presetsToExport = presets.filter(preset => selectedPresets.includes(preset.id));
      const json = JSON.stringify(presetsToExport, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "presets.json";
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const resolveDiagnostics = async (columns: Column[], presetId: string): Promise<Column[]> => {
    const resolvedColumns: Column[] = [];

    for (const column of columns) {
      // Try to resolve by ID first (more reliable than name search)
      if (column.diagnostic.id) {
        const byId = await geotabClient.get(
          "Diagnostic",
          { id: column.diagnostic.id },
          1,
        );
        if (byId.length > 0) {
          resolvedColumns.push({
            ...column,
            diagnostic: { name: byId[0].name, id: byId[0].id },
          });
          continue;
        }
      }

      // Fall back to name search
      const diagnostics = column.diagnostic.name
        ? await getDiagnostics(geotabClient, column.diagnostic.name, undefined, 10, true)
        : [];

      if (!diagnostics || diagnostics.length === 0) {
        alert(`Could not find diagnostic "${column.diagnostic.name}" (id: ${column.diagnostic.id})`);
        return [];
      } else if (diagnostics.length > 1) {
        const selected = await new Promise<{ name: string; id: string } | null>(resolve => {
          setMultipleMatchesDialog({
            open: true,
            options: diagnostics.map(d => ({ name: d.name, id: d.id })),
            column,
            presetId,
            resolve,
          });
        });

        if (selected) {
          resolvedColumns.push({
            ...column,
            diagnostic: selected,
          });
        } else {
          alert("Something went wrong");
          return [];
        }
      } else {
        resolvedColumns.push({
          ...column,
          diagnostic: diagnostics[0],
        });
      }
    }

    return resolvedColumns;
  };

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async e => {
        try {
          const importedPresets: Preset[] = JSON.parse(e.target?.result as string);
          setImportInProgress(true);

          for (const preset of importedPresets) {
            if (presets.find(p => p.description === (preset?.description ?? ""))) {
              alert(`Preset "${preset.description}" already exists.`);
              continue;
            }
            const resolvedColumns = await resolveDiagnostics(preset.columns, preset.id);
            if (resolvedColumns.length !== preset.columns.length) {
              alert(
                `Preset "${preset.description || "Unnamed"}" was not imported due to unresolved issues.`,
              );
              continue;
            }

            await updateAddIn.mutateAsync(
              diagnosticsToPreset(
                preset.description ?? "",
                resolvedColumns.map(col => col.diagnostic) as Diagnostic[],
                preset.groups?.[0]?.id || "",
              ),
            );
          }
        } catch (error) {
          console.error("Failed to import presets:", error);
          alert("Failed to import presets.");
        } finally {
          setImportInProgress(false);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleResolveOption = () => {
    const selectedDiagnostic = multipleMatchesDialog.options.find(
      option => option.id === selectedOption,
    );
    multipleMatchesDialog.resolve(selectedDiagnostic || null);
    setMultipleMatchesDialog({
      open: false,
      options: [],
      column: null,
      presetId: null,
      resolve: () => {
        return;
      },
    });
    setSelectedOption(null);
  };

  const handleCancelOption = () => {
    if (multipleMatchesDialog.resolve) {
      multipleMatchesDialog.resolve(null);
    }
    setMultipleMatchesDialog({
      open: false,
      options: [],
      column: null,
      presetId: null,
      resolve: () => {
        return;
      },
    });
  };

  const handleDelete = () => {
    for (const presetId of selectedPresets) {
      removeAddIn.mutateAsync({ delSearch: presetId });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button variant="contained" size="small" onClick={handleExport} disabled={selectedPresets.length === 0}>
          {t("admin.presetManager.export")}
        </Button>
        <Button variant="contained" size="small" component="label" loading={importInProgress}>
          {t("admin.presetManager.import")}
          <input type="file" accept="application/json" hidden onChange={handleImport} />
        </Button>
        <Button variant="contained" size="small" onClick={handleDelete} disabled={selectedPresets.length === 0}>
          {t("admin.presetManager.delete")}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ height: TABLE_HEIGHT }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Typography color="error">{t("admin.presetManager.loadError")}</Typography>
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedPresets.length > 0 && selectedPresets.length < (presets?.length || 0)
                    }
                    checked={presets?.length > 0 && selectedPresets.length === presets.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t("admin.presetManager.columnDescription")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {presets?.map(preset => {
                const isItemSelected = selectedPresets.includes(preset.id);
                return (
                  <TableRow
                    key={preset.id}
                    hover
                    onClick={() => handleClick(preset.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell>{preset.description || "Unnamed Preset"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Unresolved Diagnostics Dialog */}
      <Dialog open={multipleMatchesDialog.open}>
        <DialogTitle>{"Resolve Diagnostic"}</DialogTitle>
        <DialogContent>
          <Typography>
            {"Multiple matches found for this diagnostic. Please select one:"}
          </Typography>
          <RadioGroup value={selectedOption} onChange={e => setSelectedOption(e.target.value)}>
            {multipleMatchesDialog.options.map(option => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio />}
                label={`${option.name} (${option.id})`}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOption}>{"Cancel"}</Button>
          <Button variant="contained" onClick={handleResolveOption} disabled={!selectedOption}>
            {"Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PresetBulkManager;
